export class AgingSystem {
  // Age-related constants
  static DAYS_PER_MONTH = 32 // Game months are 32 days
  static MONTHS_PER_YEAR = 12
  static DAYS_PER_YEAR = AgingSystem.DAYS_PER_MONTH * AgingSystem.MONTHS_PER_YEAR // 384 days
  
  // Performance curve constants
  static PEAK_AGE_MONTHS = 30 // 2.5 years
  static MIN_PERFORMANCE_MODIFIER = 0.7 // At very old age
  static MAX_PERFORMANCE_MODIFIER = 1.0 // At peak age
  static YOUNG_PERFORMANCE_MODIFIER = 0.8 // At birth
  
  /**
   * Calculate exact age in days for a chocobo
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {number} Age in days
   */
  static getAgeInDays(chocobo, currentDay) {
    if (chocobo.birthDay && chocobo.birthDay > 0) {
      // Stable-born chocobo with known birth day
      return Math.max(0, currentDay - chocobo.birthDay)
    } else if (chocobo.estimatedBirthDay) {
      // Wild chocobo with estimated birth day
      return Math.max(0, currentDay - chocobo.estimatedBirthDay)
    } else {
      // Fallback: estimate based on level (older save compatibility)
      return this.estimateAgeFromLevel(chocobo.level)
    }
  }
  
  /**
   * Calculate age in months
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {number} Age in months
   */
  static getAgeInMonths(chocobo, currentDay) {
    const ageInDays = this.getAgeInDays(chocobo, currentDay)
    return Math.floor(ageInDays / this.DAYS_PER_MONTH)
  }
  
  /**
   * Get formatted age string
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @param {boolean} approximate - Whether to show "~" for estimated ages
   * @returns {string} Formatted age (e.g., "2y 4m", "8 months", "~1y 2m")
   */
  static getFormattedAge(chocobo, currentDay, approximate = true) {
    const ageInMonths = this.getAgeInMonths(chocobo, currentDay)
    const years = Math.floor(ageInMonths / this.MONTHS_PER_YEAR)
    const months = ageInMonths % this.MONTHS_PER_YEAR
    
    // Determine if age is estimated
    const isEstimated = approximate && (!chocobo.birthDay || chocobo.estimatedBirthDay)
    const prefix = isEstimated ? "~" : ""
    
    if (years === 0) {
      return `${prefix}${ageInMonths} month${ageInMonths === 1 ? '' : 's'}`
    } else if (months === 0) {
      return `${prefix}${years}y`
    } else {
      return `${prefix}${years}y ${months}m`
    }
  }
  
  /**
   * Calculate performance modifier based on age
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {number} Performance modifier (0.7 to 1.0)
   */
  static getAgePerformanceModifier(chocobo, currentDay) {
    const ageInMonths = this.getAgeInMonths(chocobo, currentDay)
    
    if (ageInMonths <= this.PEAK_AGE_MONTHS) {
      // Young to peak: gradual improvement from 0.8 to 1.0
      const progress = ageInMonths / this.PEAK_AGE_MONTHS
      return this.YOUNG_PERFORMANCE_MODIFIER + 
             (this.MAX_PERFORMANCE_MODIFIER - this.YOUNG_PERFORMANCE_MODIFIER) * 
             this.smoothCurve(progress)
    } else {
      // Past peak: gradual decline from 1.0 to 0.7
      const maxDeclineAge = 96 // 8 years
      const declineProgress = Math.min(1, (ageInMonths - this.PEAK_AGE_MONTHS) / (maxDeclineAge - this.PEAK_AGE_MONTHS))
      return this.MAX_PERFORMANCE_MODIFIER - 
             (this.MAX_PERFORMANCE_MODIFIER - this.MIN_PERFORMANCE_MODIFIER) * 
             this.smoothCurve(declineProgress)
    }
  }
  
  /**
   * Smooth curve function for natural aging progression
   * @param {number} x - Progress value (0 to 1)
   * @returns {number} Smoothed value
   */
  static smoothCurve(x) {
    // Use easing function for natural curve
    return x * x * (3 - 2 * x) // Smoothstep function
  }
  
  /**
   * Estimate age from level for older saves
   * @param {number} level - Chocobo level
   * @returns {number} Estimated age in days
   */
  static estimateAgeFromLevel(level) {
    // Rough estimate: assume average training progression
    // Higher levels = older chocobos
    const estimatedMonths = Math.max(6, level * 2 + Math.random() * 12)
    return Math.floor(estimatedMonths * this.DAYS_PER_MONTH)
  }
  
  /**
   * Generate estimated birth day for wild chocobos
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {number} Estimated birth day
   */
  static generateEstimatedBirthDay(chocobo, currentDay) {
    if (chocobo.birthDay && chocobo.birthDay > 0) {
      // Already has a known birth day
      return chocobo.birthDay
    }
    
    // Estimate based on level and stats
    const baseAge = this.estimateAgeFromLevel(chocobo.level)
    // Add some randomness for variety
    const variance = Math.floor(Math.random() * 60) - 30 // ±30 days
    const estimatedAge = Math.max(30, baseAge + variance) // At least 30 days old
    
    return Math.max(1, currentDay - estimatedAge)
  }
  
  /**
   * Check if chocobo is in their prime years
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {boolean} Whether chocobo is in prime
   */
  static isInPrime(chocobo, currentDay) {
    const ageInMonths = this.getAgeInMonths(chocobo, currentDay)
    return ageInMonths >= 18 && ageInMonths <= 42 // 1.5 to 3.5 years
  }
  
  /**
   * Get age category for display
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {string} Age category
   */
  static getAgeCategory(chocobo, currentDay) {
    const ageInMonths = this.getAgeInMonths(chocobo, currentDay)
    
    if (ageInMonths < 12) return "Young"
    if (ageInMonths < 18) return "Adolescent"
    if (ageInMonths < 42) return "Prime"
    if (ageInMonths < 72) return "Mature"
    return "Veteran"
  }
  
  /**
   * Calculate training effectiveness modifier based on age
   * Young chocobos learn faster, older chocobos learn slower
   * @param {Object} chocobo - The chocobo object
   * @param {number} currentDay - Current game day
   * @returns {number} Training modifier (0.6 to 1.4)
   */
  static getAgeTrainingModifier(chocobo, currentDay) {
    const ageInMonths = this.getAgeInMonths(chocobo, currentDay)
    
    if (ageInMonths <= 12) {
      // Young chocobos (0-1 year): Learn very fast (1.2x to 1.4x)
      const progress = ageInMonths / 12
      return 1.4 - (0.2 * this.smoothCurve(progress))
    } else if (ageInMonths <= 18) {
      // Adolescent chocobos (1-1.5 years): Learn fast (1.0x to 1.2x)
      const progress = (ageInMonths - 12) / 6
      return 1.2 - (0.2 * this.smoothCurve(progress))
    } else if (ageInMonths <= 48) {
      // Prime/mature chocobos (1.5-4 years): Normal learning (1.0x)
      return 1.0
    } else if (ageInMonths <= 72) {
      // Mature to veteran transition (4-6 years): Declining learning (1.0x to 0.8x)
      const progress = (ageInMonths - 48) / 24
      return 1.0 - (0.2 * this.smoothCurve(progress))
    } else {
      // Very old chocobos (6+ years): Learn slowly (0.8x to 0.6x)
      const maxOldAge = 120 // 10 years
      const progress = Math.min(1, (ageInMonths - 72) / (maxOldAge - 72))
      return 0.8 - (0.2 * this.smoothCurve(progress))
    }
  }
}